import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const MAX_MS = 15000;

/**
 * VoiceIntroRecorder — 15-second voice intro recorder + player.
 * Uses the browser MediaRecorder API (no external library).
 */
export const VoiceIntroRecorder = ({ user, onChange }) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startRef = useRef(0);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Your browser doesn't support voice recording.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const dur = Date.now() - startRef.current;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        await upload(blob, dur);
      };
      recorderRef.current = rec;
      startRef.current = Date.now();
      setElapsed(0);
      rec.start();
      setRecording(true);
      timerRef.current = setInterval(() => {
        const el = Date.now() - startRef.current;
        setElapsed(el);
        if (el >= MAX_MS) stop();
      }, 100);
    } catch (err) {
      toast.error("Microphone permission denied.");
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const upload = async (blob, durationMs) => {
    if (durationMs < 1000) {
      toast.error("Intro too short — try at least 1 second.");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      const { data } = await api.post("/profile/voice-intro", {
        data_url: dataUrl,
        duration_ms: Math.min(durationMs, MAX_MS + 500),
      });
      onChange?.(data);
      toast.success("Voice intro saved");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save voice intro"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/profile/voice-intro");
      onChange?.(data);
      toast.success("Voice intro removed");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't remove voice intro"));
    } finally {
      setBusy(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
  };

  const hasIntro = !!user?.voice_intro_url;
  const remaining = Math.max(0, MAX_MS - elapsed);

  return (
    <div className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-6" data-testid="voice-intro-panel">
      <div className="flex items-center gap-2">
        <Mic className="w-5 h-5 text-[#722f37]" />
        <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Voice intro</span>
        <span className="ml-auto text-[11px] text-[#5c5c5c]">15 seconds max</span>
      </div>
      <p className="mt-2 text-sm text-[#5c5c5c]">
        A warm, 15-second voice intro plays right on your Top Rated card — members hear your voice before they reveal.
      </p>

      {hasIntro && !recording && (
        <div className="mt-4 flex items-center gap-3 rounded-xl bg-[#faf9f6] border border-[#e5e0d8] p-3" data-testid="voice-intro-current">
          <button
            type="button"
            onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-[#722f37] text-white flex items-center justify-center hover:bg-[#8c3a45] transition-colors"
            data-testid="voice-intro-play"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </button>
          <div className="flex-1 text-sm text-[#1a1a1a] font-medium">
            Your intro · {Math.round((user.voice_intro_duration_ms || 0) / 1000)}s
          </div>
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="text-xs text-[#a02b2b] hover:underline flex items-center gap-1"
            data-testid="voice-intro-remove"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Remove
          </button>
          <audio
            ref={audioRef}
            src={user.voice_intro_url}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            preload="none"
          />
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        {!recording ? (
          <button
            type="button"
            onClick={start}
            disabled={busy}
            className="btn-primary flex items-center gap-2 !py-2 !px-4 text-sm"
            data-testid="voice-intro-record"
          >
            <Mic className="w-4 h-4" /> {hasIntro ? "Re-record" : "Record"}
          </button>
        ) : (
          <button
            type="button"
            onClick={stop}
            className="flex items-center gap-2 rounded-full bg-[#a02b2b] text-white px-4 py-2 text-sm font-medium hover:bg-[#b23838]"
            data-testid="voice-intro-stop"
          >
            <Square className="w-4 h-4 fill-current" /> Stop
          </button>
        )}
        {recording && (
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-[#a02b2b] animate-pulse" />
            <span className="font-mono tabular-nums text-[#1a1a1a]" data-testid="voice-intro-timer">
              {(elapsed / 1000).toFixed(1)}s
            </span>
            <span className="text-xs text-[#5c5c5c]">/ 15.0s</span>
            <div className="ml-2 w-32 h-1.5 rounded-full bg-[#eae5d9] overflow-hidden">
              <div className="h-full bg-[#a02b2b] transition-all" style={{ width: `${(elapsed / MAX_MS) * 100}%` }} />
            </div>
          </div>
        )}
        {busy && !recording && <Loader2 className="w-4 h-4 animate-spin text-[#722f37]" />}
      </div>
    </div>
  );
};

/**
 * VoiceIntroPlayer — compact player used on Top Rated cards and profiles viewers see.
 */
export const VoiceIntroPlayer = ({ url, durationMs, testId }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  if (!url) return null;
  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
  };
  return (
    <button
      type="button"
      onClick={toggle}
      className="inline-flex items-center gap-1.5 rounded-full bg-white/95 text-[#722f37] border border-[#c39d2b]/50 px-2.5 py-1 text-[11px] font-semibold hover:bg-white shadow-sm transition-colors"
      data-testid={testId || "voice-intro-play-btn"}
      aria-label={playing ? "Pause voice intro" : "Play voice intro"}
    >
      {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
      <span>Voice · {Math.round((durationMs || 0) / 1000) || 15}s</span>
      <audio
        ref={audioRef}
        src={url}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        preload="none"
      />
    </button>
  );
};

export default VoiceIntroRecorder;
